import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Download, Plus, Trash2, Gamepad2, RefreshCw, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { getProjectPlayables, deletePlayable, type PlayableListItem } from '@/api/modules/playables';
import { createBatch } from '@/api/modules/batch';
import { cn } from '@/utils/cn';
import { CreateBatchDialog } from '@/components/CreateBatchDialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useAgentStore } from '@/store/agent-store';

const MODE_LABELS: Record<string, string> = {
  'ai-chat': 'AI生成',
  'batch-variant': '批量变体',
  'template': '模板',
  'blueprint': '蓝图模式',
  'video': '视频转试玩',
  'cocos-source': 'Cocos源码',
  // BUG-20260807-PW-003 兼容 GENERATION_MODE_OPTIONS 的 'cocos' 值
  'cocos': 'Cocos源码',
  'cocos-generation': 'Cocos生成',
};

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(dateStr: string): string {
  // 统一完整时间格式：YYYY/MM/DD HH:mm:ss
  return new Date(dateStr).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

interface PlayableListTabProps {
  projectId: string;
  onSwitchToWorkspace?: (sessionId: string, playableId: string) => void;
}

export function PlayableListTab({ projectId, onSwitchToWorkspace }: PlayableListTabProps): JSX.Element {
  const navigate = useNavigate();
  const [playables, setPlayables] = useState<PlayableListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<PlayableListItem | null>(null);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchPlayableId, setBatchPlayableId] = useState<string | undefined>(undefined);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProjectPlayables(projectId);
      const published = res.data.filter(p => p.status === 'published');
      setPlayables(published);
      // 自动选中第一个
      if (!selected && published.length > 0) setSelected(published[0]);
      else if (selected && !published.find(p => p.id === selected.id)) setSelected(published[0] ?? null);
    } catch {
      toast.error('加载试玩列表失败');
    } finally {
      setLoading(false);
    }
  }, [projectId, selected]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // 微调完成（独立微调图产出新版本）→ 自动刷新列表；playableId 每次微调唯一，幂等
  const refineDoneId = useAgentStore((s) => (s.refineState.status === 'done' ? s.refineState.playableId : undefined));
  useEffect(() => { if (refineDoneId) fetchList(); }, [refineDoneId]);

  const { confirm, dialog } = useConfirm();

  const handleDelete = (playable: PlayableListItem) => {
    confirm('删除试玩', `确定删除试玩 ${playable.variantLabel || `V${playable.version}`}？删除后无法恢复。`, () => {
      deletePlayable(playable.id).then(() => {
        toast.success('已删除');
        if (selected?.id === playable.id) setSelected(null);
        fetchList();
      }).catch(() => toast.error('删除失败'));
    });
  };

  const handleBatchCreate = async (desc: string, count: number, sourcePlayableId?: string, projectIdParam?: string, dimensions?: Array<{ name: string; values: string[] }>) => {
    try {
      await createBatch({ description: desc, count, sourcePlayableId, projectId: projectIdParam, dimensions });
      toast.success('已创建批量变体任务', {
        description: '进度和生成结果请前往「创意工厂」查看',
        action: {
          label: '前往创意工厂',
          onClick: () => navigate('/batch'),
        },
      });
      setShowBatchDialog(false);
      setBatchPlayableId(undefined);
    } catch {
      toast.error('创建失败，请确认后端服务已启动');
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* 顶部说明 */}
      <div className="border-b border-border/60 bg-card/20 px-4 py-2">
        <p className="text-xs text-muted-foreground">
          项目下所有已发布的试玩版本 · 点击"查看源会话"可切换到对应的工作区查看生成过程
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Version list */}
        <div className="flex w-72 shrink-0 flex-col border-r border-border/60 bg-card/40">
        <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground/90">版本列表</span>
          <button onClick={fetchList} disabled={loading} className="icon-btn rounded p-1" title="刷新">
            <RefreshCw size={12} className={loading ? 'animate-spin text-neon-cyan' : ''} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {playables.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <Gamepad2 size={24} className="opacity-40" />
              <span className="text-xs">暂无试玩版本</span>
              <span className="text-[10px] opacity-60">通过 AI 对话生成试玩</span>
            </div>
          ) : (
            playables.map((p) => {
              const score = p.quality?.score;
              const isSelected = selected?.id === p.id;
              const modeLabel = p.generationMode?.trim() ? MODE_LABELS[p.generationMode.trim()] ?? p.generationMode : '未知';
              const modeColor = p.generationMode === 'batch-variant' ? 'text-neon-purple bg-neon-purple/15' : 'bg-muted/60 text-muted-foreground';
              return (
                <button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all',
                    isSelected
                      ? 'border-primary/50 bg-primary/10 shadow-sm'
                      : 'border-border/30 bg-muted/20 hover:border-border/60 hover:bg-muted/40',
                  )}
                >
                  {/* 封面缩略图（9:16 竖版封面，异步生成，加载失败自动隐藏） */}
                  {p.coverUrl && (
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md border border-border/40 bg-muted/30">
                      <img
                        src={p.coverUrl}
                        alt={`V${p.version} 封面`}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="truncate text-sm font-medium text-foreground">
                        {p.name || `V${p.version}${p.variantLabel ? ` · ${p.variantLabel}` : ''}`}
                      </span>
                      {typeof score === 'number' && (
                        <span className={cn('text-[10px] font-medium', score >= 90 ? 'text-neon-green' : score >= 70 ? 'text-neon-cyan' : 'text-amber-400')}>
                          {score}分
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span className={cn('rounded px-1 py-0.5', modeColor)}>{modeLabel}</span>
                      <span>{timeAgo(p.createdAt)}</span>
                      {p.sourcePlayableId && <span className="text-primary/70">← 变体</span>}
                      {p.sessionId && <span className="text-muted-foreground/60" title={`会话 ID: ${p.sessionId}`}>会话</span>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Detail panel */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {!selected ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <span className="text-sm">{playables.length > 0 ? '请选择一个版本' : '暂无试玩版本'}</span>
          </div>
        ) : (
          <>
            {/* Preview */}
            <div className="flex-1 overflow-auto p-4">
              <div className="mx-auto max-w-md">
                {selected.previewUrl ? (
                  <div className="overflow-hidden rounded-xl border border-border/40">
                    <iframe
                      src={selected.previewUrl}
                      className="h-[640px] w-full border-0"
                      sandbox="allow-scripts allow-same-origin"
                      title={`Playable V${selected.version}`}
                    />
                  </div>
                ) : (
                  <div className="flex h-64 items-center justify-center rounded-xl border border-border/30 bg-muted/20">
                    <span className="text-xs text-muted-foreground">暂无预览</span>
                  </div>
                )}

                {/* Metadata card */}
                <div className="mt-3 glass-card p-3 space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/80">版本信息</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div><span className="text-muted-foreground">版本号：</span><span className="text-foreground">V{selected.version}</span></div>
                    <div><span className="text-muted-foreground">构建模式：</span><span className="text-foreground">{MODE_LABELS[selected.generationMode ?? ''] ?? selected.generationMode ?? '-'}</span></div>
                    <div><span className="text-muted-foreground">大小：</span><span className="text-foreground">{formatFileSize(selected.fileSize)}</span></div>
                    {selected.variantLabel && <div className="col-span-2"><span className="text-muted-foreground">标签：</span><span className="text-foreground">{selected.variantLabel}</span></div>}
                    {selected.sourcePlayableId && <div className="col-span-2"><span className="text-muted-foreground">创建方式：</span><span className="text-primary">批量变体</span></div>}
                    <div className="col-span-2"><span className="text-muted-foreground">创建时间：</span><span className="text-foreground">{new Date(selected.createdAt).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span></div>
                    {typeof selected.quality?.score === 'number' && (
                      <div className="col-span-2"><span className="text-muted-foreground">AI评分：</span><span className={cn('font-medium', selected.quality.score >= 90 ? 'text-neon-green' : 'text-neon-cyan')}>{selected.quality.score}</span></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex shrink-0 items-center gap-2 border-t border-border/60 px-4 py-2">
              {onSwitchToWorkspace && selected.sessionId && (
                <button
                  onClick={() => onSwitchToWorkspace(selected.sessionId, selected.id)}
                  className="icon-btn flex items-center gap-1 rounded-lg border border-neon-cyan/50 bg-neon-cyan/10 px-3 py-1.5 text-xs hover:border-neon-cyan hover:bg-neon-cyan/20"
                  title="切换到源会话工作区并定位到该版本">
                  <MessageSquare size={12} />
                  查看源会话
                </button>
              )}
              <button onClick={() => selected.previewUrl && window.open(selected.previewUrl, '_blank')}
                disabled={!selected.previewUrl}
                className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-3 py-1.5 text-xs disabled:opacity-30 hover:border-primary/50">
                <ExternalLink size={12} />预览
              </button>
              <button onClick={() => selected.previewUrl && window.open(`/api/playable/${selected.sessionId}/download`, '_blank')}
                disabled={!selected.previewUrl}
                className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-3 py-1.5 text-xs disabled:opacity-30 hover:border-primary/50">
                <Download size={12} />下载
              </button>
              <button onClick={() => { setBatchPlayableId(selected.id); setShowBatchDialog(true); }}
                className="glow-btn flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium"
                title="创建批量任务">
                <Plus size={12} />
              </button>
              <button onClick={() => handleDelete(selected)}
                className="icon-btn danger flex items-center gap-1 rounded-lg border border-border/50 px-3 py-1.5 text-xs ml-auto hover:border-red-500/50">
                <Trash2 size={12} />删除
              </button>
            </div>
          </>
        )}
      </div>
      </div>

      <CreateBatchDialog
        open={showBatchDialog}
        onClose={() => { setShowBatchDialog(false); setBatchPlayableId(undefined); }}
        onCreate={handleBatchCreate}
        initialPlayableId={batchPlayableId}
      />
      {dialog}
    </div>
  );
}
